import { Shape, ShapeType } from '@/services/shape';
import { useLayerStore } from '@/store/use-layer-store';
import { useEditorStore } from '@/store/use-editor-store';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { canUse } from '@/utils/can-use.utils';
import { useImagesStore } from '@/store/use-images-store';

export function useSlotFollow() {
  const layerStore = useLayerStore();
  const editorStore = useEditorStore();
  const watchStore = useWatchfaceStore();
  const imageStore = useImagesStore();

  /**
   * 容器跟随选项
   */
  const findFollowingSlotOptions = () => {
    const firstThemeSlots =
      layerStore.themes[0]?.children?.filter(
        (item) => item.type === ShapeType.Slot
      ) || [];
    const options = firstThemeSlots.map((slot) => ({
      label: slot.attrs.name,
      value: slot.id
    }));
    return [{ label: '不跟随', value: 'none' }, ...options];
  };

  /**
   * 当前容器是否正在跟随母容器
   * @param slotFollowingId 跟随的容器id
   */
  const judgeIfFollowingSlot = (slotFollowingId?: string) => {
    return (
      !!findFollowingSlot(slotFollowingId) && judgeIsSupportSlotFollowing()
    );
  };

  /**
   * 找到当前正在跟随的母容器id
   * @param slotFollowingId 跟随的容器id
   */
  const findFollowingSlot = (slotFollowingId?: string) => {
    if (!slotFollowingId || !canUse('support-slot-following').valid) return;
    const firstThemeSlots =
      layerStore.themes[0]?.children?.filter(
        (item) => item.type === ShapeType.Slot
      ) || [];
    return firstThemeSlots.find((item) => item.id == slotFollowingId)?.id;
  };

  /**
   * 当前容器是否支持容器跟随
   */
  const judgeIsSupportSlotFollowing = () => {
    return (
      canUse('support-slot-following').valid &&
      watchStore.slotFollowing &&
      editorStore.theme?.id !== layerStore.themes[0].id
    );
  };

  /**
   * 找到跟随的母容器的groupname
   * @param slotFollowingId 跟随的容器id
   */
  const findParentSlotGroupName = (slotFollowingId: string) => {
    const slotShape = Shape.getShape(slotFollowingId);
    if (!slotShape) return;

    if (!slotShape.attrs.followingGroupName) {
      const allSlots = layerStore.themes[0].children.filter(
        (child) => child.type === 'Slot'
      );
      const count =
        allSlots.filter((slot) => slot.attrs?.followingGroupName).length + 1;

      slotShape.setAttrs({
        followingGroupName: String(count)
      });
      return slotShape.attrs.followingGroupName;
    }
  };

  /**
   * @description 母容器属性变化时，跟随的子容器属性也变化
   * @param shape Shape
   */
  const followParentAttrs = (
    parentShape: Shape,
    attrName: string,
    attrVal: any,
    shapeType = ShapeType.Slot,
    widgetIndex = 0
  ) => {
    const allSlots = layerStore.themes.flatMap((theme) =>
      theme.children.filter((child) => child.type === 'Slot')
    );
    const followingSlots = allSlots.filter(
      (slot) => slot.attrs.slotFollowingId == parentShape.id
    );

    followingSlots.forEach((slot) => {
      const childShape = Shape.getShape(slot.id);
      if (!childShape) return;

      if (shapeType === ShapeType.Slot) {
        if (attrName === 'widgetIdToDisplay') {
          if (attrVal == 'none') {
            childShape.setAttrs({ [attrName]: 'none' });
            return;
          }
          const parentWidgetIndex = parentShape.children.findIndex(
            (child) => child.id == attrVal
          );
          const followWidgetDisplayId =
            childShape.children[parentWidgetIndex]?.id;
          childShape.setAttrs({ [attrName]: followWidgetDisplayId });
          return;
        }

        if (attrName === 'includeEmpty') {
          if (attrVal) {
            const basename = imageStore.addEmptyImage();
            childShape.setAttrs({
              [attrName]: !!attrVal,
              emptyImageSrc: basename
            });
          } else {
            const widgetIdToDisplay = childShape.attrs.widgetIdToDisplay;
            childShape.setAttrs({
              [attrName]: !!attrVal,
              emptyImageSrc: '',
              widgetIdToDisplay:
                widgetIdToDisplay == 'none' ? '' : widgetIdToDisplay
            });
          }
          return;
        }

        if (attrName === 'uploadEditbox' || attrName === 'deleteEditbox') {
          const childTheme = layerStore.themes.find((theme) =>
            theme.children.some((child) => child.id === slot.id)
          );
          const parentColorGroup =
            editorStore.theme?.colorGroupTable?.map((child) => child.id) || [];
          const curColorIndex = parentColorGroup.indexOf(
            editorStore.themeColor?.id ?? ''
          );
          const childColorGroup =
            childTheme?.colorGroupTable?.map((child) => child.id) || [];
          const colorGroup = childColorGroup[curColorIndex];

          if (attrName === 'uploadEditbox') {
            // 统一边框关闭时，widgetIndex >= 0 表示上传到子容器中对应索引的小部件
            if (widgetIndex >= 0) {
              const followWidget = childShape.children[widgetIndex];
              const followWidgetShape = followWidget
                ? Shape.getShape(followWidget.id)
                : undefined;
              followWidgetShape?.uploadResource({
                resourceName: 'editBox',
                colorGroup,
                realpath: attrVal,
                dirname: '_widget'
              });
            } else {
              childShape.uploadResource({
                resourceName: 'editBox',
                colorGroup,
                realpath: attrVal,
                dirname: '_widget'
              });
            }
          } else {
            // 统一边框关闭时，widgetIndex >= 0 表示删除子容器中对应索引的小部件边框图
            if (widgetIndex >= 0) {
              const followWidget = childShape.children[widgetIndex];
              const followWidgetShape = followWidget
                ? Shape.getShape(followWidget.id)
                : undefined;
              followWidgetShape?.deleteResource({
                resourceName: 'editBox',
                colorGroup
              });
            } else {
              childShape.deleteResource({
                resourceName: 'editBox',
                colorGroup
              });
            }
          }
          return;
        }
        childShape.setAttrs({ [attrName]: attrVal });
      } else if (shapeType === ShapeType.Widget) {
        const widget = childShape.children[widgetIndex];
        if (widget) {
          if (
            [
              'jumpApp',
              'relatedDataKey',
              'jumpAppSportTypeId',
              'jumpAppSportId',
              'minClickZone',
              'jumpAppCustomName',
              'jumpAppAlipayContent'
            ].includes(attrName)
          ) {
            widget.setAttrs({
              [attrName]: attrVal
            });
          }
        }
      }
    });
  };

  /**
   * @description 母容器里的组移动时，跟随的子容器也跟随移动
   * @param parentShape 母容器
   * @param widgetIndex 母容器的widget索引
   * return {shapeId: string, parentId: string, themeId: string}
   */
  const followParentMove = (
    parentShape: Shape,
    widgetIndex = 0
  ): Array<{
    shapeId: string | undefined;
    parentId: string | undefined;
    themeId: string | undefined;
  }> => {
    const allSlots = layerStore.themes.flatMap((theme) =>
      theme.children.filter((child) => child.type === 'Slot')
    );
    const followingSlots = allSlots.filter(
      (slot) => slot.attrs.slotFollowingId == parentShape.id
    );
    const results = [];
    for (const slot of followingSlots) {
      const childShape = Shape.getShape(slot.id);
      const widget = childShape?.children[widgetIndex];
      if (widget) {
        const themeId = layerStore.themes.find((theme) =>
          theme.children.some((child) => child.id === slot.id)
        )?.id;
        results.push({ shapeId: widget.id, parentId: slot.id, themeId });
      }
    }
    return results;
  };

  /**
   * @description 设置跟随后，子容器的followid设置
   * @param followId 跟随的容器id
   * @param shape Shape
   * @param parendId 母级id
   */
  const setFollowingId = (
    followId: string,
    shape: Shape,
    parendId?: string
  ) => {
    if (!shape) return;
    if (shape.type === ShapeType.Slot) {
      shape.setAttrs({
        slotFollowingId: followId == 'none' ? undefined : followId
      });
      if (followId !== 'none') {
        findParentSlotGroupName(followId);
        // 设置跟随时，同步父容器的 sameEditBox，includeEmpty 等属性到子容器
        const parentShape = Shape.getShape(followId);
        if (parentShape) {
          const includeEmpty = !!parentShape.attrs.includeEmpty;
          shape.setAttrs({
            sameEditBox: parentShape.attrs.sameEditBox,
            includeEmpty,
            emptyImageSrc: includeEmpty ? imageStore.addEmptyImage() : ''
          });
        }
      }
      shape?.children?.forEach((child, index) => {
        setFollowingId(child.id, shape.children[index], followId);
      });
    } else if (shape.type === ShapeType.Widget) {
      shape.setAttrs({
        slotFollowingId: parendId == 'none' ? undefined : parendId
      });
    }
  };

  /**
   * @description 删除跟随的子容器跟随属性
   * @param followId 跟随的容器id
   * @returns
   */
  const deleteFollowingSlotAttr = (followId: string) => {
    const allSlots = layerStore.themes.flatMap((theme) =>
      theme.children.filter((child) => child.type === 'Slot')
    );
    const followingSlots = allSlots.filter(
      (slot) => slot.attrs.slotFollowingId == followId
    );

    followingSlots.forEach((slot) => {
      const slotShape = Shape.getShape(slot.id);
      slotShape?.setAttrs({ slotFollowingId: undefined });
      delete slot.attrs.slotFollowingId;

      slot.children?.forEach((child) => {
        const childShape = Shape.getShape(child.id);
        if (childShape?.attrs.slotFollowingId) {
          childShape.setAttrs({ slotFollowingId: undefined });
          delete child.attrs.slotFollowingId;
        }
      });
    });
  };

  return {
    findFollowingSlotOptions,
    judgeIfFollowingSlot,
    findFollowingSlot,
    judgeIsSupportSlotFollowing,
    findParentSlotGroupName,
    setFollowingId,
    deleteFollowingSlotAttr,
    followParentAttrs,
    followParentMove
  };
}
